import React, {useState} from 'react';
import {Pressable, StyleSheet, Modal, ScrollView, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {formatCurrency} from '@/Utils/CommonUtils';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

interface Pricing {
  duration_months: number;
  per_month_price?: string;
  base_price?: string;
  price_per_session?: string;
  per_session_price?: string;
  roadmap?: {
    duration_months: number;
    total_price: string;
    monthly_equivalent: string;
    incomplete: boolean;
    remaining_months: number;
    segments: Array<{
      pricing_id: number;
      level_slug: string | null;
      level_name: string | null;
      format_slug: string | null;
      format_name: string | null;
      coach_tier_code: string | null;
      coach_tier_name: string | null;
      tournament_id: number | null;
      months_used: number;
      price_per_session: string;
      segment_total: string;
      currency_code: string;
    }>;
  };
  currency?: string;
  currency_code?: string;
  plan_type: string;
  format_slug?: string;
}

interface DurationSelectorProps {
  selectedDuration: number;
  onSelect: (duration: number) => void;
  pricingOptions: Pricing[];
  isMobile: boolean;
  selectedCoach?:
    | 'general'
    | 'fide_master'
    | 'international_master'
    | 'grand_master';
  availableDurations?: number[];
  durationDiscounts?: {
    [tournamentId: string]: {
      [durationMonths: number]: number; // discount percentage
    };
  };
  tournamentId?: number | null;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({
  selectedDuration,
  onSelect,
  pricingOptions,
  isMobile,
  selectedCoach = 'general',
  availableDurations,
  durationDiscounts,
  tournamentId,
}) => {
  const {isMobileOrTab} = useMediaQuery();
  const [expandedDuration, setExpandedDuration] = useState<number | null>(null);
  const [roadmapModal, setRoadmapModal] = useState<{
    visible: boolean;
    title: string;
    segments:
      | Array<{
          pricing_id: number;
          level_slug: string | null;
          level_name: string | null;
          format_slug: string | null;
          format_name: string | null;
          coach_tier_code: string | null;
          coach_tier_name: string | null;
          tournament_id: number | null;
          months_used: number;
          price_per_session: string;
          segment_total: string;
          currency_code: string;
        }>
      | undefined;
    totalPrice?: string;
    currency?: string;
  }>({
    visible: false,
    title: '',
    segments: undefined,
    totalPrice: undefined,
    currency: undefined,
  });
  // No coach premium multiplier - backend prices already include coach tier pricing
  const coachPremiumMultiplier = 1.0;

  // Use availableDurations from API if provided, otherwise derive from pricingOptions, fallback to default
  const durationsToShow =
    availableDurations && availableDurations.length > 0
      ? availableDurations
      : pricingOptions.length > 0
      ? pricingOptions
          .map(p => p.duration_months)
          .filter((d, i, arr) => arr.indexOf(d) === i)
      : [3, 6, 12]; // Default fallback

  // Get pricing info for display (use first pricing option as template if available)
  const templatePricing = pricingOptions.length > 0 ? pricingOptions[0] : null;
  const planType = templatePricing?.plan_type || 'personal';
  const currency =
    templatePricing?.currency || templatePricing?.currency_code || 'INR';

  return (
    <>
      <Box marginBottom={30}>
        <CCText
          fontSize={FontSizes[18]}
          fontWeight="bold"
          marginBottom={15}
          color={CCColors.TextColor.Dark}>
          Duration
        </CCText>
        <Box flexDirection="row" gap={15} flexWrap="wrap">
          {durationsToShow.map(duration => {
            const isSelected = selectedDuration === duration;
            const isBestSeller = duration === 6; // Hardcoded for now based on design
            // Calculate sessions: 8 sessions per month (3 months = 24 sessions)
            const sessions = duration * 8;

            // Find pricing for this duration if available
            const pricing = pricingOptions.find(
              p => p.duration_months === duration,
            );

            // Calculate discount percentage for this duration
            let discountPct = 0;
            if (durationDiscounts && tournamentId) {
              const tournamentIdStr = tournamentId.toString();
              if (
                durationDiscounts[tournamentIdStr] &&
                durationDiscounts[tournamentIdStr][duration]
              ) {
                discountPct = durationDiscounts[tournamentIdStr][duration];
              } else if (
                durationDiscounts['null'] &&
                durationDiscounts['null'][duration]
              ) {
                discountPct = durationDiscounts['null'][duration];
              }
            }

            return (
              <Pressable
                key={duration}
                style={[
                  styles.durationCard,
                  isSelected && styles.durationCardSelected,
                  isBestSeller && styles.bestSellerCard,
                  {
                    flex: isMobile ? undefined : 1,
                    width: isMobile ? '100%' : undefined,
                    maxWidth: isMobile ? undefined : 250,
                  },
                ]}
                onPress={() => onSelect(duration)}>
                {/* Best Seller Badge - Top Left */}
                {isBestSeller && (
                  <View
                    style={[
                      styles.bestSellerBadge,
                      {
                        position: 'absolute',
                        top: -8,
                        left: 12,
                        backgroundColor: CCColors.Orange,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                        borderRadius: 12,
                        zIndex: 10,
                      },
                    ]}>
                    <CCText
                      fontSize={FontSizes[9]}
                      color={CCColors.White}
                      fontWeight="bold">
                      BEST SELLER
                    </CCText>
                  </View>
                )}

                {/* Discount Percentage Badge - Top Right */}
                {discountPct > 0 && (
                  <View
                    style={[
                      styles.discountBadge,
                      {
                        position: 'absolute',
                        top: -8,
                        right: 12,
                        backgroundColor: CCColors.Green,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                        borderRadius: 12,
                        zIndex: 10,
                      },
                    ]}>
                    <CCText
                      fontSize={FontSizes[14]}
                      color={CCColors.White}
                      fontWeight="bold">
                      {discountPct}% OFF
                    </CCText>
                  </View>
                )}

                <Box marginBottom={15} type='row-flex-start' gap={10} alignItems='flex-start'>
                  {isSelected ? (
                    <Box
                      width={20}
                      height={20}
                      borderRadius={10}
                      backgroundColor={CCColors.Primary.Default}
                      justifyContent="center"
                      alignItems="center"
                      marginBottom={10}>
                      <MaterialCommunityIcons
                        name="check"
                        size={14}
                        color={CCColors.TextColor.Dark}
                      />
                    </Box>
                  ) : (
                    <Box
                      width={20}
                      height={20}
                      borderRadius={10}
                      borderWidth={2}
                      borderColor={CCColors.Grey.Divider200}
                      marginBottom={10}
                    />
                  )}
                  <Box marginBottom={5}>
                    <CCText
                      fontSize={FontSizes[16]}
                      fontWeight="bold"
                      color={CCColors.TextColor.Dark}>
                      {sessions} Session{sessions !== 1 ? 's' : ''}
                    </CCText>
                    <CCText
                      fontSize={FontSizes[12]}
                      color={CCColors.TextColor.Grey}>
                      {duration} Months
                    </CCText>
                  </Box>
                </Box>

                {/* Price section hidden - shown in mobile Next button instead */}

                {pricing?.roadmap?.segments &&
                  pricing.roadmap.segments.length > 0 && (
                    <Box marginTop={8}>
                      <Box flexDirection="row" flexWrap="wrap" gap={6} alignItems="center">
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          gap={4}
                          marginBottom={4}>
                          <MaterialCommunityIcons
                            name="map-outline"
                            size={14}
                            color={CCColors.TextColor.Grey}
                          />
                          <CCText
                            fontSize={FontSizes[11]}
                            color={CCColors.TextColor.Grey}
                            fontWeight="semibold">
                            Roadmap:
                          </CCText>
                        </Box>
                        {pricing.roadmap.segments.map((seg, idx) => {
                          const segments = pricing.roadmap!.segments;
                          const isLast = idx === segments.length - 1;
                          return (
                            <Box
                              key={`${seg.pricing_id}_${idx}`}
                              flexDirection="row"
                              alignItems="center"
                              gap={4}>
                              <Box
                                flexDirection="row"
                                alignItems="center"
                                gap={4}
                                paddingHorizontal={10}
                                paddingVertical={5}
                                backgroundColor={CCColors.Primary.lightBrown}
                                borderRadius={8}
                                borderWidth={1}
                                borderColor={CCColors.Primary.Default}
                                style={styles.roadmapChip}>
                                <MaterialCommunityIcons
                                  name="check-circle"
                                  size={12}
                                  color={CCColors.Primary.Default}
                                />
                                <CCText
                                  fontSize={FontSizes[11]}
                                  color={CCColors.TextColor.Dark}
                                  fontWeight="semibold"
                                  numberOfLines={1}>
                                  {seg.level_name || seg.level_slug || 'Level'}
                                </CCText>
                              </Box>
                              {!isLast && (
                                <MaterialCommunityIcons
                                  name="chevron-right"
                                  size={14}
                                  color={CCColors.Primary.Default}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                {isSelected &&
                  pricing?.roadmap?.segments &&
                  pricing.roadmap.segments.length > 0 && (
                    <Box marginTop={10}>
                      <Pressable
                        onPress={() =>
                          setRoadmapModal({
                            visible: true,
                            title: `${duration} Months Roadmap`,
                            segments: pricing.roadmap?.segments,
                            totalPrice: pricing.roadmap?.total_price,
                            currency:
                              pricing?.currency ||
                              pricing?.currency_code ||
                              currency,
                          })
                        }>
                        <CCText
                          fontSize={FontSizes[12]}
                          color={CCColors.Primary.Brown}
                          fontWeight="semibold">
                          View details
                        </CCText>
                      </Pressable>
                    </Box>
                  )}
              </Pressable>
            );
          })}
        </Box>
      </Box>

      {/* Roadmap Modal */}
      <Modal
        visible={roadmapModal.visible}
        transparent={true}
        animationType="slide"
        onRequestClose={() =>
          setRoadmapModal({
            visible: false,
            title: '',
            segments: undefined,
            totalPrice: undefined,
            currency: undefined,
          })
        }>
        <Box
          flex={1}
          justifyContent="center"
          alignItems="center"
          backgroundColor="rgba(0,0,0,0.4)">
          <Box
            width={isMobileOrTab ? '90%' : '500px'}
            maxHeight="70%"
            backgroundColor={CCColors.White}
            borderRadius={12}
            padding={16}
            borderWidth={1}
            borderColor={CCColors.Grey.Divider200}>
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={12}>
              <CCText
                fontSize={FontSizes[16]}
                fontWeight="bold"
                color={CCColors.TextColor.Dark}>
                {roadmapModal.title || 'Roadmap'}
              </CCText>
              <Pressable
                onPress={() =>
                  setRoadmapModal({
                    visible: false,
                    title: '',
                    segments: undefined,
                    totalPrice: undefined,
                    currency: undefined,
                  })
                }>
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors.Primary.Brown}
                  fontWeight="semibold">
                  Close
                </CCText>
              </Pressable>
            </Box>
            <ScrollView>
              {roadmapModal.segments && roadmapModal.segments.length > 0 ? (
                <>
                  {/* Table Header */}
                  <Box
                    flexDirection="row"
                    backgroundColor={CCColors.Primary.lightBrown}
                    padding={12}
                    borderRadius={8}
                    marginBottom={8}
                    borderBottomWidth={2}
                    borderBottomColor={CCColors.Primary.Default}>
                    <Box flex={2} style={styles.tableHeaderCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}>
                        Level
                      </CCText>
                    </Box>
                    <Box flex={1.5} style={styles.tableHeaderCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}>
                        Duration
                      </CCText>
                    </Box>
                    <Box flex={2} style={styles.tableHeaderCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}>
                        Format
                      </CCText>
                    </Box>
                    <Box flex={1.5} style={styles.tableHeaderCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}
                        textAlign="right">
                        Price
                      </CCText>
                    </Box>
                  </Box>

                  {/* Table Rows */}
                  {roadmapModal.segments.map((seg, segIdx) => {
                    const durationText =
                      seg.months_used === 1
                        ? `${seg.months_used} month`
                        : `${seg.months_used} months`;

                    return (
                      <Box
                        key={`${seg.pricing_id}_${segIdx}`}
                        flexDirection="row"
                        padding={12}
                        borderBottomWidth={1}
                        borderBottomColor={CCColors.Grey.Divider200}
                        alignItems="center">
                        <Box flex={2} style={styles.tableCell}>
                          <CCText
                            fontSize={FontSizes[13]}
                            color={CCColors.TextColor.Dark}
                            fontWeight="semibold">
                            {seg.level_name || seg.level_slug || 'N/A'}
                          </CCText>
                        </Box>
                        <Box flex={1.5} style={styles.tableCell}>
                          <CCText
                            fontSize={FontSizes[13]}
                            color={CCColors.TextColor.Dark}>
                            {durationText}
                          </CCText>
                        </Box>
                        <Box flex={2} style={styles.tableCell}>
                          <CCText
                            fontSize={FontSizes[13]}
                            color={CCColors.TextColor.Dark}>
                            {seg.format_name || seg.format_slug || 'N/A'}
                          </CCText>
                        </Box>
                        <Box flex={1.5} style={styles.tableCell}>
                          <CCText
                            fontSize={FontSizes[13]}
                            color={CCColors.TextColor.Dark}
                            textAlign="right"
                            fontWeight="semibold">
                            {formatCurrency(
                              parseFloat(seg.segment_total || '0'),
                              seg.currency_code ||
                                roadmapModal.currency ||
                                'INR',
                            )}
                          </CCText>
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Total Price Row */}
                  {roadmapModal.totalPrice && (
                    <Box
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      padding={16}
                      marginTop={8}
                      backgroundColor={CCColors.Primary.lightBrown}
                      borderRadius={8}
                      borderTopWidth={2}
                      borderTopColor={CCColors.Primary.Default}>
                      <CCText
                        fontSize={FontSizes[16]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}>
                        Total Price
                      </CCText>
                      <CCText
                        fontSize={FontSizes[18]}
                        fontWeight="bold"
                        color={CCColors.Primary.Default}>
                        {formatCurrency(
                          parseFloat(roadmapModal.totalPrice),
                          roadmapModal.currency || 'INR',
                        )}
                      </CCText>
                    </Box>
                  )}
                </>
              ) : (
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors.TextColor.Grey}>
                  No roadmap available.
                </CCText>
              )}
            </ScrollView>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  durationCard: {
    backgroundColor: CCColors.White,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CCColors.Grey.Divider200,
    justifyContent: 'space-between',
  },
  durationCardSelected: {
    borderColor: CCColors.TextColor.Dark,
    borderWidth: 2,
    backgroundColor: CCColors.Primary.lightBrown,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 8,
  },
  bestSellerCard: {
    borderColor: CCColors.Orange,
    borderWidth: 1,
  },
  bestSellerBadge: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  discountBadge: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  tableHeaderCell: {
    paddingHorizontal: 4,
  },
  tableCell: {
    paddingHorizontal: 4,
  },
  roadmapChip: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
});

export default DurationSelector;
